/**
 * Browser detection utilities to help with browser-specific optimizations
 * Used to adjust rendering settings based on the browser
 */
import { browserLogger } from './logger';

// Import WebGLPowerPreference type for proper typing
type WebGLPowerPreference = 'high-performance' | 'low-power' | 'default';

// Safe browser environment detection
export const isBrowser = typeof window !== 'undefined';

// Only access navigator if in browser environment
const getUserAgent = (): string => {
  if (!isBrowser) return '';
  return navigator.userAgent || '';
};

const userAgent = getUserAgent();

// Basic browser detection
export const isChrome = isBrowser && /Chrome/.test(userAgent) && !/Edge|Edg/.test(userAgent);
export const isFirefox = isBrowser && /Firefox/.test(userAgent);
export const isSafari = isBrowser && /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
export const isEdge = isBrowser && /Edge|Edg/.test(userAgent);
export const isIE = isBrowser && /MSIE|Trident/.test(userAgent);

// Log browser information once during initialization
if (isBrowser) {
  browserLogger.debug('Browser detected', {
    userAgent,
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
    isIE
  });
}

// Chrome version detection
export const getChromeVersion = (): number => {
  if (!isChrome) return 0;
  
  const match = userAgent.match(/Chrome\/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Hardware/device info - improved to detect DevTools mobile mode
export const isMobileDevice = isBrowser && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

// Check if using a mobile viewport size (for DevTools testing)
export const isMobileViewport = isBrowser && window.innerWidth < 768;

// Combined check that works both with real devices and DevTools
export const isMobile = isMobileDevice || isMobileViewport;

export const isIOS = isBrowser && /iPhone|iPad|iPod/i.test(userAgent);
export const isAndroid = isBrowser && /Android/i.test(userAgent);

/**
 * Performance profile types that can be used to adjust rendering settings
 */
export enum PerformanceProfile {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high'
}

/**
 * Get the appropriate performance profile based on browser and device
 * @returns The recommended performance profile
 */
export function getPerformanceProfile(): PerformanceProfile {
  // Low-end devices always get low profile
  if (isMobile) {
    return PerformanceProfile.LOW;
  }
  
  // Chrome-specific handling (the focus of our optimization)
  if (isChrome) {
    const chromeVersion = getChromeVersion();
    
    // Newer Chrome versions may have better optimization
    if (chromeVersion >= 90) {
      return PerformanceProfile.MEDIUM;
    }
    
    // Older Chrome gets lowest settings
    return PerformanceProfile.LOW;
  }
  
  // Other browsers get higher performance settings
  if (isFirefox || isEdge) {
    return PerformanceProfile.HIGH;
  }
  
  if (isSafari) {
    return PerformanceProfile.MEDIUM;
  }
  
  // Default to medium for unknown browsers
  return PerformanceProfile.MEDIUM;
}

/**
 * Get browser-specific rendering options
 * @returns Object with rendering preferences for the current browser
 */
export function getBrowserRenderingOptions(): {
  antialias: boolean;
  powerPreference: WebGLPowerPreference;
  alpha: boolean;
  stencil: boolean;
  depth: boolean;
  premultipliedAlpha: boolean;
  targetFps: number;
  useRAF: boolean;
  maxTextureSize: number;
  maxLights: number;
  maxParticles: number;
  useHardwareAcceleration: boolean;
  useWillChange: boolean;
} {
  return {
    // WebGL rendering options
    antialias: !isChrome, // Chrome often performs better without antialiasing
    powerPreference: (isChrome ? 'default' : 'high-performance') as WebGLPowerPreference,
    alpha: true,
    stencil: !isChrome, // Not needed in Chrome for our neural network
    depth: true,
    premultipliedAlpha: false,
    
    // Animation settings
    targetFps: isChrome ? 30 : 60,
    useRAF: true, // Use requestAnimationFrame
    
    // Resource limits
    maxTextureSize: isChrome ? 2048 : 4096,
    maxLights: isChrome ? 2 : 4,
    maxParticles: isChrome ? 1000 : 5000,
    
    // CSS optimizations
    useHardwareAcceleration: true,
    useWillChange: isChrome // Especially important for Chrome
  };
}

/**
 * Get detailed information about the current browser environment
 */
export function getBrowserEnvironmentInfo(): {
  browser: string;
  version: number;
  os: string;
  isMobile: boolean;
  supportsWebGL: boolean;
  supportsWebGL2: boolean;
  hasHardwareAcceleration?: boolean;
  devicePixelRatio?: number;
  viewport?: { width: number; height: number };
  memoryInfo?: { jsHeapSizeLimit?: number; totalJSHeapSize?: number; usedJSHeapSize?: number } | null;
} {
  if (!isBrowser) {
    return {
      browser: 'server',
      version: 0,
      os: 'server',
      isMobile: false,
      supportsWebGL: false,
      supportsWebGL2: false
    };
  }

  // Check for WebGL support
  let supportsWebGL = false;
  let supportsWebGL2 = false;
  
  try {
    const canvas = document.createElement('canvas');
    supportsWebGL = !!(window.WebGLRenderingContext && 
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    supportsWebGL2 = !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
  } catch (e) {
    browserLogger.warn('Error checking WebGL support:', e);
  }

  // OS detection
  let os = 'unknown';
  if (/Windows/.test(userAgent)) os = 'windows';
  else if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) os = 'mac';
  else if (/Linux/.test(userAgent)) os = 'linux';
  else if (/Android/.test(userAgent)) os = 'android';
  else if (/iPhone|iPad|iPod/.test(userAgent)) os = 'ios';

  // Get viewport and device info safely
  const devicePixelRatio = isBrowser ? (window.devicePixelRatio || 1) : 1;
  const viewport = isBrowser ? {
    width: window.innerWidth || 0,
    height: window.innerHeight || 0
  } : { width: 0, height: 0 };

  // Memory info
  let memoryInfo: { jsHeapSizeLimit?: number, totalJSHeapSize?: number, usedJSHeapSize?: number } | null = null; 
  // Use more specific check for performance.memory
  const performanceWithMemory = window.performance as Performance & { memory?: { jsHeapSizeLimit?: number, totalJSHeapSize?: number, usedJSHeapSize?: number } };
  if (isBrowser && performanceWithMemory.memory) { 
    try {
      memoryInfo = {
        // Add optional chaining or checks for each property
        jsHeapSizeLimit: performanceWithMemory.memory?.jsHeapSizeLimit,
        totalJSHeapSize: performanceWithMemory.memory?.totalJSHeapSize,
        usedJSHeapSize: performanceWithMemory.memory?.usedJSHeapSize
      };
    } catch (e) {
      browserLogger.warn('Error accessing memory info:', e);
    }
  }

  const result = {
    browser: isChrome ? 'chrome' : 
             isFirefox ? 'firefox' : 
             isSafari ? 'safari' : 
             isEdge ? 'edge' : 
             isIE ? 'ie' : 'unknown',
    version: isChrome ? getChromeVersion() : 0,
    os,
    isMobile,
    supportsWebGL,
    supportsWebGL2,
    hasHardwareAcceleration: supportsWebGL && !isMobile,
    devicePixelRatio,
    viewport,
    memoryInfo
  };

  browserLogger.debug('Detailed browser environment info', result);
  return result;
}

/**
 * Checks if the current device likely has low memory (less than 4GB).
 * Uses the deviceMemory API when available, falls back to mobile detection.
 * @returns boolean True if the device likely has low memory
 */
export const hasLowMemory = (): boolean => {
  if (!isBrowser) return false;
  
  // Use the deviceMemory API if available
  if ('deviceMemory' in navigator) {
    const deviceMemory = (navigator as Navigator & { deviceMemory: number }).deviceMemory;
    return deviceMemory < 4; // Less than 4GB is considered low memory
  }
  
  // Fall back to mobile detection - most mobile devices have less RAM
  return isMobile;
};

/**
 * Sets up a resize listener to properly update the isMobile state when using DevTools
 * @param callback Optional callback to run when mobile state changes
 */
export const setupResponsiveListener = (callback?: (isMobile: boolean) => void): { remove: () => void } => {
  if (!isBrowser) return { remove: () => { /* Empty by design */ } };
  
  // Initialize with current state
  let currentIsMobile = isMobileViewport;
  
  // Create the resize handler
  const handleResize = (): void => {
    const newIsMobile = window.innerWidth < 768;
    
    // Only trigger if changed
    if (newIsMobile !== currentIsMobile) {
      currentIsMobile = newIsMobile;
      
      // Skip setting global state via window object to avoid ESLint warnings
      
      // Call the callback if provided
      if (callback) {
        callback(newIsMobile);
      }
      
      // Log for debugging
      browserLogger.debug('Mobile viewport state changed', { isMobile: newIsMobile, width: window.innerWidth });
    }
  };
  
  // Set up listener
  window.addEventListener('resize', handleResize);
  
  // Return a cleanup function
  return {
    remove: () => {
      window.removeEventListener('resize', handleResize);
    }
  };
};

// Initialize responsive detection on import
if (isBrowser) {
  setupResponsiveListener();
} 
